import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Form,
  message,
  Typography,
  Popconfirm,
  Tooltip,
  Badge,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  LockOutlined,
  BankOutlined,
  CrownOutlined,
  PhoneOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { userService } from "../../../services/userService";
import { companyService } from "../../../services/companyService";
import { authService } from "../../../services/authService";

const { Title, Text } = Typography;
const { Option } = Select;

const Users = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pageCache, setPageCache] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [companyFilter, setCompanyFilter] = useState("all");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = currentUser?.role === "super_admin";

  // Load companies for super_admin
  const loadCompanies = async () => {
    if (!isSuperAdmin) return;

    try {
      setCompaniesLoading(true);
      const response = await companyService.getCompanies(1, 1000);
      if (response.status) {
        setCompanies(response.data.companies || []);
      }
    } catch (error) {
      console.error("Load companies error:", error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // Load users with caching
  const loadUsers = async (
    page = 1,
    pageSize = 10,
    search = searchText,
    role = roleFilter,
    status = statusFilter,
    forceRefresh = false,
  ) => {
    try {
      setLoading(true);

      // Create a cache key based on all filters
      const cacheKey = `${page}-${pageSize}-${search}-${role}-${status}`;

      // Check if we have cached data for this page and not forcing refresh
      if (!forceRefresh && pageCache[cacheKey]) {
        setUsers(pageCache[cacheKey].data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: pageCache[cacheKey].total,
        });
        setLoading(false);
        return;
      }

      const response = await userService.getUsers(
        page,
        pageSize,
        search,
        role,
        status,
      );

      if (response.status) {
        // Store in cache
        setPageCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: response.data.users || [],
            total: response.data.pagination?.totalUsers || 0,
          },
        }));

        setUsers(response.data.users || []);
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: pageSize,
          total: response.data.pagination?.totalUsers || 0,
        });
      } else {
        message.error(response.message || "Failed to load users");
        setUsers([]);
        setPagination({
          current: 1,
          pageSize: pageSize,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Load users error:", error);
      message.error(error.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load on component mount
  useEffect(() => {
    loadUsers(1, pagination.pageSize, searchText, roleFilter, statusFilter);
    if (isSuperAdmin) {
      loadCompanies();
    }
  }, []);

  // Handle filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Clear all cache when filters change
      setPageCache({});
      loadUsers(1, pagination.pageSize, searchText, roleFilter, statusFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, roleFilter, statusFilter]);

  const handleCompanyFilter = (value) => {
    setCompanyFilter(value);
  };

  // Filter users locally for company filter only
  const filteredUsers = users.filter((user) => {
    if (isSuperAdmin && companyFilter !== "all") {
      if (companyFilter === "none") {
        if (user.companyId) return false;
      } else {
        if (user.companyId !== companyFilter) return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          {record.role === "super_admin" ? (
            <CrownOutlined style={{ color: "#722ed1" }} />
          ) : record.role === "admin" ? (
            <UserOutlined style={{ color: "#ff4d4f" }} />
          ) : (
            <UserOutlined />
          )}
          <div>
            <div className="font-medium">{text}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => {
        const roleColors = {
          super_admin: "purple",
          admin: "red",
          user: "blue",
        };
        const roleLabels = {
          super_admin: "Super Admin",
          admin: "Admin",
          user: "User",
        };
        return (
          <Tag color={roleColors[role] || "blue"}>
            {roleLabels[role] || role.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (company, record) => (
        <Space>
          {company ? (
            <>
              <BankOutlined style={{ color: "#1890ff" }} />
              <div>
                <div className="font-medium">{company.name}</div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  ID: {record.companyId}
                </Text>
              </div>
            </>
          ) : (
            <Text type="secondary">No company assigned</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Badge
          status={status === "active" ? "success" : "error"}
          text={
            <Tag color={status === "active" ? "green" : "red"}>
              {status.toUpperCase()}
            </Tag>
          }
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <div>
          <div>{new Date(date).toLocaleDateString()}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {new Date(date).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Text>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const canEdit =
          isSuperAdmin ||
          (currentUser?.role === "admin" &&
            record.companyId === currentUser.companyId);

        const canDelete =
          isSuperAdmin ||
          (currentUser?.role === "admin" &&
            record.companyId === currentUser.companyId &&
            record.id !== currentUser.id);

        return (
          <Space size="middle">
            <Tooltip title="Edit User">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
            </Tooltip>

            <Popconfirm
              title="Delete User"
              description="Are you sure you want to delete this user? This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              okType="danger"
            >
              <Tooltip title="Delete User">
                <Button type="link" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleAddUser = () => {
    const autoPassword = generatePassword();
    const initialValues = {
      password: autoPassword,
      confirmPassword: autoPassword,
    };

    if (currentUser?.role === "admin") {
      initialValues.companyId = currentUser.companyId;
    }

    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (values.password !== values.confirmPassword) {
        message.error("Passwords do not match!");
        return;
      }

      const userData = {
        name: values.name,
        email: values.email,
        password: values.password,
        mobileNumber: values.mobileNumber,
        role: values.role,
      };

      if (values.companyId) {
        userData.companyId = values.companyId;
      }

      const response = await userService.createUser(userData);

      if (response.status) {
        message.success("User created successfully!");
        setIsModalVisible(false);
        form.resetFields();

        // Clear cache
        setPageCache({});

        // Reset company filter to show the new user
        if (isSuperAdmin) {
          setCompanyFilter("all");
        }

        // Reload from page 1
        await loadUsers(
          1,
          pagination.pageSize,
          searchText,
          roleFilter,
          statusFilter,
          true,
        );
      } else {
        message.error(response.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Create user error:", error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || "Failed to create user");
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      name: user.name,
      email: user.email,
      mobileNumber: user.mobileNumber,
      role: user.role,
      status: user.status,
      companyId: user.companyId,
      password: "",
      confirmPassword: "",
    });
    setIsEditModalVisible(true);
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();

      const updateData = {
        name: values.name,
        email: values.email,
        mobileNumber: values.mobileNumber,
        role: values.role,
        status: values.status,
      };

      if (isSuperAdmin && values.companyId) {
        updateData.companyId = values.companyId;
      }

      if (values.password && values.password === values.confirmPassword) {
        updateData.password = values.password;
      }

      const response = await userService.updateUser(editingUser.id, updateData);

      if (response.status) {
        message.success("User updated successfully!");
        setIsEditModalVisible(false);
        editForm.resetFields();
        setEditingUser(null);

        // Clear cache
        setPageCache({});

        // Reset company filter to show the updated user
        if (isSuperAdmin) {
          setCompanyFilter("all");
        }

        // Reload from page 1 with force refresh
        await loadUsers(
          1,
          pagination.pageSize,
          searchText,
          roleFilter,
          statusFilter,
          true,
        );
      } else {
        message.error(response.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Update user error:", error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || "Failed to update user");
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    editForm.resetFields();
    setEditingUser(null);
  };

  const handleDelete = async (userId) => {
    try {
      const response = await userService.deleteUser(userId);
      if (response.status) {
        message.success("User deleted successfully!");

        // Clear cache
        setPageCache({});

        // Determine which page to load after deletion
        let targetPage = pagination.current;

        // If this was the last item on the page and not on first page, go to previous page
        if (filteredUsers.length === 1 && pagination.current > 1) {
          targetPage = pagination.current - 1;
        }

        // Reload with force refresh
        await loadUsers(
          targetPage,
          pagination.pageSize,
          searchText,
          roleFilter,
          statusFilter,
          true,
        );
      } else {
        message.error(response.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Delete user error:", error);
      message.error(error.message || "Failed to delete user");
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    form.setFieldsValue({
      password: newPassword,
      confirmPassword: newPassword,
    });
    message.info("New password generated!");
  };

  const handleTableChange = (newPagination) => {
    loadUsers(
      newPagination.current,
      newPagination.pageSize,
      searchText,
      roleFilter,
      statusFilter,
    );
  };

  const handleRefresh = () => {
    // Clear cache for current page and force refresh
    const currentCacheKey = `${pagination.current}-${pagination.pageSize}-${searchText}-${roleFilter}-${statusFilter}`;
    setPageCache((prev) => {
      const newCache = { ...prev };
      delete newCache[currentCacheKey];
      return newCache;
    });
    loadUsers(
      pagination.current,
      pagination.pageSize,
      searchText,
      roleFilter,
      statusFilter,
      true,
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
  };

  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return [
        { value: "user", label: "User - Can submit testimonials" },
        { value: "admin", label: "Admin - Company administrator" },
      ];
    } else if (currentUser?.role === "admin") {
      return [{ value: "user", label: "User - Can submit testimonials" }];
    }
    return [];
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2}>User Management</Title>
          <Text type="secondary">
            {isSuperAdmin
              ? "Manage all users across all companies"
              : currentUser?.role === "admin"
                ? "Manage users in your company"
                : "View your profile"}
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="large"
          >
            Refresh
          </Button>
          {(isSuperAdmin || currentUser?.role === "admin") && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
              size="large"
            >
              Add User
            </Button>
          )}
        </Space>
      </div>

      <Card className="shadow-sm">
        <div className="flex justify-between mb-4 flex-wrap gap-2">
          <Input
            placeholder="Search users by name or email..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            size="large"
            allowClear
          />
          <Space>
            <Select
              value={roleFilter}
              onChange={handleRoleFilter}
              style={{ width: 150 }}
              size="large"
              allowClear
            >
              <Option value="all">All Roles</Option>
              <Option value="admin">Admin</Option>
              <Option value="user">User</Option>
            </Select>
            <Select
              value={statusFilter}
              onChange={handleStatusFilter}
              style={{ width: 150 }}
              size="large"
              allowClear
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
            {isSuperAdmin && (
              <Select
                value={companyFilter}
                onChange={handleCompanyFilter}
                style={{ width: 200 }}
                size="large"
                placeholder="Filter by Company"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                <Option value="all">All Companies</Option>
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          pagination={{
            ...pagination,
            total: filteredUsers.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} users`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          rowKey="id"
        />
      </Card>

      {/* Add User Modal */}
      <Modal
        title="Add New User"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Create User"
        cancelText="Cancel"
        width={600}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" name="addUserForm">
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Please input the name!" }]}
          >
            <Input
              size="large"
              placeholder="Enter full name"
              prefix={<UserOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please input the email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input
              size="large"
              placeholder="Enter email address"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: "Please input the mobile number!" },
              {
                pattern: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit mobile number!",
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Enter mobile number"
              prefix={<PhoneOutlined />}
              maxLength={10}
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role!" }]}
          >
            <Select size="large" placeholder="Select role">
              {getAvailableRoles().map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {isSuperAdmin && (
            <Form.Item
              name="companyId"
              label="Company"
              rules={[{ required: true, message: "Please select a company!" }]}
            >
              <Select
                size="large"
                placeholder="Select company"
                loading={companiesLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <div className="flex justify-between items-center mb-2">
            <Text strong>Password</Text>
            <Button
              type="link"
              onClick={handleGeneratePassword}
              icon={<LockOutlined />}
            >
              Generate Password
            </Button>
          </div>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please input the password!" },
              { min: 6, message: "Password must be at least 6 characters!" },
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Enter password"
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: "Please confirm the password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match!"));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Confirm password"
              prefix={<LockOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={`Edit User - ${editingUser?.name}`}
        open={isEditModalVisible}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Update User"
        cancelText="Cancel"
        width={600}
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical" name="editUserForm">
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Please input the name!" }]}
          >
            <Input
              size="large"
              placeholder="Enter full name"
              prefix={<UserOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please input the email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input
              size="large"
              placeholder="Enter email address"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: "Please input the mobile number!" },
              {
                pattern: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit mobile number!",
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Enter mobile number"
              prefix={<PhoneOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role!" }]}
          >
            <Select
              size="large"
              placeholder="Select role"
              disabled={!isSuperAdmin}
            >
              {getAvailableRoles().map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {isSuperAdmin && (
            <Form.Item
              name="companyId"
              label="Company"
              rules={[{ required: true, message: "Please select a company!" }]}
            >
              <Select
                size="large"
                placeholder="Select company"
                loading={companiesLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status!" }]}
          >
            <Select size="large" placeholder="Select status">
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-between items-center mb-2">
            <Text strong>Change Password (Optional)</Text>
            <Button
              type="link"
              onClick={() => {
                const newPassword = generatePassword();
                editForm.setFieldsValue({
                  password: newPassword,
                  confirmPassword: newPassword,
                });
                message.info("New password generated!");
              }}
              icon={<LockOutlined />}
            >
              Generate Password
            </Button>
          </div>

          <Form.Item
            name="password"
            rules={[
              { min: 6, message: "Password must be at least 6 characters!" },
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Leave empty to keep current password"
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match!"));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Confirm new password"
              prefix={<LockOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
